/**
 * Entity Descriptors Registration
 *
 * This module registers all domain entity descriptors with the EntityRegistry.
 * Import this module during application bootstrap to enable entity dictionary features.
 *
 * @example
 * ```typescript
 * // In apps/console/src/bootstrap.ts
 * import { registerDescriptors } from '@syscolect/domain';
 *
 * bootstrapDataSources();
 * registerDescriptors(); // Register entity descriptors
 * ```
 */
import { entityRegistry } from '@rammetec/data-access';
// Import descriptors
import { userDataDescriptor, organizationUserOrgDataDescriptor, } from './user.descriptor.js';
import { organizationDataDescriptor, userOrganizationDescriptor, } from './organization.descriptor.js';
import { profileDataDescriptor, userProfileDescriptor, } from './profile.descriptor.js';
import { moduleDescriptor, organizationModuleDescriptor, themeSettingDescriptor } from './module.descriptor.js';
import { planDescriptor } from './plan.descriptor.js';
import { moduleGroupDescriptor } from './moduleGroup.descriptor.js';
import { productDescriptor } from './product.descriptor.js';
import { productGroupDescriptor } from './product-group.descriptor.js';
import { brandDescriptor } from './brand.descriptor.js';
import { savedSelectionDescriptor } from './saved-selection.descriptor.js';
import { sectorDescriptor } from './sector.descriptor.js';
import { factoryDescriptor } from './factory.descriptor.js';
import { productionStopReasonDescriptor } from './production-stop-reason.descriptor.js';
import { operationDescriptor } from './operation.descriptor.js';
import { machineDescriptor } from './machine.descriptor.js';
import { machineGroupDescriptor } from './machine-group.descriptor.js';
import { processDescriptor } from './process.descriptor.js';
import { componentDescriptor } from './component.descriptor.js';
import { globalConfigDescriptor, sessionDescriptor, } from './system.descriptor.js';
import { integrationConfigDescriptor } from './integration.descriptor.js';
import { ticketWorkspaceDescriptor, ticketWorkflowStageDescriptor, ticketAutomationRuleDescriptor, ticketAutomationExecutionDescriptor, } from './ticket-workspace.descriptor.js';
import { workspaceTicketDescriptor, ticketMessageDescriptor, ticketAttachmentDescriptor, ticketParticipantDescriptor, ticketRelationDescriptor, ticketLinkDescriptor, ticketActivityLogDescriptor, } from './workspace-ticket.descriptor.js';
import { tagDescriptor } from './tag.descriptor.js';
import { tagLinkDescriptor } from './tag-link.descriptor.js';
import { colorDescriptor } from './color.descriptor.js';
import { technicalSheetDescriptor } from './technical-sheet.descriptor.js';
import { technicalSheetRouteDescriptor } from './technical-sheet-route.descriptor.js';
import { technicalSheetMaterialDescriptor } from './technical-sheet-material.descriptor.js';
import { costCenterDescriptor } from './cost-center.descriptor.js';
import { distributionCenterDescriptor } from './distribution-center.descriptor.js';
import { stockLocationDescriptor } from './stock-location.descriptor.js';
import { storageAddressDescriptor } from './storage-address.descriptor.js';
import { storageAddressPatternDescriptor } from './storage-address-pattern.descriptor.js';
import { lotDescriptor } from './lot.descriptor.js';
import { stockMovementDescriptor } from './stock-movement.descriptor.js';
import { auditOperationDescriptor } from './audit-operation.descriptor.js';
import { auditFieldDescriptor } from './audit-field.descriptor.js';
import { stockBalanceDescriptor } from './stock-balance.descriptor.js';
import { stockLotBalanceDescriptor } from './stock-lot-balance.descriptor.js';
import { stockReservationDescriptor } from './stock-reservation.descriptor.js';
import { handlingUnitDescriptor } from './handling-unit.descriptor.js';
import { handlingUnitMovementDescriptor } from './handling-unit-movement.descriptor.js';
import { stockCountDescriptor } from './stock-count.descriptor.js';
import { stockCountEntryDescriptor } from './stock-count-entry.descriptor.js';
import { warehouseTaskTypeDescriptor } from './warehouse-task-type.descriptor.js';
import { warehouseTaskDescriptor } from './warehouse-task.descriptor.js';
import { warehouseTaskLineDescriptor } from './warehouse-task-line.descriptor.js';
import { salesOrderDescriptor } from './sales-order.descriptor.js';
import { salesOrderItemDescriptor } from './sales-order-item.descriptor.js';
import { salesOrderItemBreakdownDescriptor } from './sales-order-item-breakdown.descriptor.js';
import { productionOrderDescriptor } from './production-order.descriptor.js';
import { productionOrderBreakdownDescriptor } from './production-order-breakdown.descriptor.js';
import { productionOrderStepDescriptor } from './production-order-step.descriptor.js';
import { productionOrderMaterialDescriptor } from './production-order-material.descriptor.js';
import { productionTicketDescriptor } from './production-ticket.descriptor.js';
import { productionRunDescriptor } from './production-run.descriptor.js';
import { productionStopDescriptor } from './production-stop.descriptor.js';
import { productionEntryDescriptor } from './production-entry.descriptor.js';
import { productionEntryMaterialDescriptor } from './production-entry-material.descriptor.js';
import { partyDescriptor } from './party.descriptor.js';
import { operationMachineDescriptor } from './operation-machine.descriptor.js';
import { technicalSheetRouteMachineDescriptor } from './technical-sheet-route-machine.descriptor.js';
import { technicalSheetOverrideDescriptor } from './technical-sheet-override.descriptor.js';
import { technicalSheetOverrideProcessDescriptor } from './technical-sheet-override-process.descriptor.js';
import { technicalSheetOverrideInputDescriptor } from './technical-sheet-override-input.descriptor.js';
import { productVariantDescriptor } from './product-variant.descriptor.js';
import { variationOptionDescriptor } from './variation-option.descriptor.js';
import { unitDescriptor } from './unit.descriptor.js';
import { standardPackagingDescriptor } from './standard-packaging.descriptor.js';
import { productUnitDescriptor } from './product-unit.descriptor.js';
import { sizeGridDescriptor } from './size-grid.descriptor.js';
import { sizeOccurrenceDescriptor } from './size-occurrence.descriptor.js';
import { workCalendarDescriptor } from './work-calendar.descriptor.js';
import { workShiftDescriptor } from './work-shift.descriptor.js';
import { workCalendarExceptionDescriptor } from './work-calendar-exception.descriptor.js';
import { purchaseOrderDescriptor } from './purchase-order.descriptor.js';
import { purchaseOrderItemDescriptor } from './purchase-order-item.descriptor.js';
import { purchaseOrderItemBreakdownDescriptor } from './purchase-order-item-breakdown.descriptor.js';
import { goodsReceiptDescriptor } from './goods-receipt.descriptor.js';
import { goodsReceiptItemDescriptor } from './goods-receipt-item.descriptor.js';
import { goodsReceiptItemBreakdownDescriptor } from './goods-receipt-item-breakdown.descriptor.js';
import { shipmentDescriptor } from './shipment.descriptor.js';
import { shipmentItemDescriptor } from './shipment-item.descriptor.js';
import { shipmentItemBreakdownDescriptor } from './shipment-item-breakdown.descriptor.js';
import { invoiceDescriptor } from './invoice.descriptor.js';
import { invoiceItemDescriptor } from './invoice-item.descriptor.js';
import { invoiceImportDescriptor } from './invoice-import.descriptor.js';
import { supplierProductMapDescriptor } from './supplier-product-map.descriptor.js';
import { externalRefDescriptor } from './external-ref.descriptor.js';
import { warehouseMapDescriptor } from './warehouse-map.descriptor.js';
import { printerDescriptor } from './printer.descriptor.js';
import { aiUsageLogDescriptor, aiUsageRollupDescriptor } from './ai.descriptor.js';
import { preferenceDescriptors, PREFERENCE_ENTITY } from './preferences.descriptor.js';
import { notificationDescriptors, NOTIFICATION_ENTITY } from './notifications.descriptor.js';
/**
 * Registers all domain entity descriptors with the EntityRegistry.
 * Call this function during application bootstrap.
 *
 * Uses lazy registration to avoid loading descriptor metadata until needed.
 */
export function registerDescriptors() {
    // System domain (global entities)
    entityRegistry.register('GlobalConfig', () => globalConfigDescriptor);
    entityRegistry.register('Session', () => sessionDescriptor);
    entityRegistry.register(PREFERENCE_ENTITY.user, () => preferenceDescriptors.userPreference);
    entityRegistry.register(PREFERENCE_ENTITY.orgUser, () => preferenceDescriptors.orgUserPreference);
    entityRegistry.register(PREFERENCE_ENTITY.orgDefault, () => preferenceDescriptors.orgPreferenceDefault);
    entityRegistry.register(NOTIFICATION_ENTITY.notification, () => notificationDescriptors.notification);
    entityRegistry.register(NOTIFICATION_ENTITY.pushDevice, () => notificationDescriptors.pushDevice);
    // Identity domain
    entityRegistry.register('UserData', () => userDataDescriptor);
    entityRegistry.register('OrganizationUserOrgData', () => organizationUserOrgDataDescriptor);
    entityRegistry.register('OrganizationData', () => organizationDataDescriptor);
    entityRegistry.register('UserOrganization', () => userOrganizationDescriptor);
    entityRegistry.register('ProfileData', () => profileDataDescriptor);
    entityRegistry.register('UserProfile', () => userProfileDescriptor);
    // Modules domain
    entityRegistry.register('Module', () => moduleDescriptor);
    entityRegistry.register('OrganizationModule', () => organizationModuleDescriptor);
    entityRegistry.register('ThemeSetting', () => themeSettingDescriptor);
    entityRegistry.register('Plan', () => planDescriptor);
    entityRegistry.register('ModuleGroup', () => moduleGroupDescriptor);
    // Catalog domain
    entityRegistry.register('Product', () => productDescriptor);
    entityRegistry.register('ProductGroup', () => productGroupDescriptor);
    entityRegistry.register('Brand', () => brandDescriptor);
    entityRegistry.register('SavedSelection', () => savedSelectionDescriptor);
    entityRegistry.register('Tag', () => tagDescriptor);
    entityRegistry.register('TagLink', () => tagLinkDescriptor);
    entityRegistry.register('Color', () => colorDescriptor);
    entityRegistry.register('TechnicalSheet', () => technicalSheetDescriptor);
    entityRegistry.register('TechnicalSheetRoute', () => technicalSheetRouteDescriptor);
    entityRegistry.register('TechnicalSheetMaterial', () => technicalSheetMaterialDescriptor);
    entityRegistry.register('Sector', () => sectorDescriptor);
    entityRegistry.register('Factory', () => factoryDescriptor);
    entityRegistry.register('ProductionStopReason', () => productionStopReasonDescriptor);
    entityRegistry.register('Operation', () => operationDescriptor);
    entityRegistry.register('Machine', () => machineDescriptor);
    entityRegistry.register('MachineGroup', () => machineGroupDescriptor);
    entityRegistry.register('Process', () => processDescriptor);
    entityRegistry.register('Component', () => componentDescriptor);
    // Integration domain
    entityRegistry.register('IntegrationConfig', () => integrationConfigDescriptor);
    // Tickets domain — workspace-based model
    entityRegistry.register('TicketWorkspace', () => ticketWorkspaceDescriptor);
    entityRegistry.register('TicketWorkflowStage', () => ticketWorkflowStageDescriptor);
    entityRegistry.register('TicketAutomationRule', () => ticketAutomationRuleDescriptor);
    entityRegistry.register('TicketAutomationExecution', () => ticketAutomationExecutionDescriptor);
    entityRegistry.register('WorkspaceTicket', () => workspaceTicketDescriptor);
    entityRegistry.register('TicketMessage', () => ticketMessageDescriptor);
    entityRegistry.register('TicketAttachment', () => ticketAttachmentDescriptor);
    entityRegistry.register('TicketParticipant', () => ticketParticipantDescriptor);
    entityRegistry.register('TicketRelation', () => ticketRelationDescriptor);
    entityRegistry.register('TicketLink', () => ticketLinkDescriptor);
    entityRegistry.register('TicketActivityLog', () => ticketActivityLogDescriptor);
    // Orders / cost centers / stock catalogs
    entityRegistry.register('CostCenter', () => costCenterDescriptor);
    entityRegistry.register('DistributionCenter', () => distributionCenterDescriptor);
    entityRegistry.register('StockLocation', () => stockLocationDescriptor);
    entityRegistry.register('StorageAddress', () => storageAddressDescriptor);
    entityRegistry.register('StorageAddressPattern', () => storageAddressPatternDescriptor);
    entityRegistry.register('Lot', () => lotDescriptor);
    entityRegistry.register('StockMovement', () => stockMovementDescriptor);
    entityRegistry.register('AuditOperation', () => auditOperationDescriptor);
    entityRegistry.register('AuditField', () => auditFieldDescriptor);
    entityRegistry.register('StockBalance', () => stockBalanceDescriptor);
    entityRegistry.register('StockLotBalance', () => stockLotBalanceDescriptor);
    entityRegistry.register('StockReservation', () => stockReservationDescriptor);
    entityRegistry.register('HandlingUnit', () => handlingUnitDescriptor);
    entityRegistry.register('HandlingUnitMovement', () => handlingUnitMovementDescriptor);
    entityRegistry.register('StockCount', () => stockCountDescriptor);
    entityRegistry.register('StockCountEntry', () => stockCountEntryDescriptor);
    entityRegistry.register('WarehouseTaskType', () => warehouseTaskTypeDescriptor);
    entityRegistry.register('WarehouseTask', () => warehouseTaskDescriptor);
    entityRegistry.register('WarehouseTaskLine', () => warehouseTaskLineDescriptor);
    entityRegistry.register('SalesOrder', () => salesOrderDescriptor);
    entityRegistry.register('SalesOrderItem', () => salesOrderItemDescriptor);
    entityRegistry.register('SalesOrderItemBreakdown', () => salesOrderItemBreakdownDescriptor);
    entityRegistry.register('ProductionOrder', () => productionOrderDescriptor);
    entityRegistry.register('ProductionOrderBreakdown', () => productionOrderBreakdownDescriptor);
    entityRegistry.register('ProductionOrderStep', () => productionOrderStepDescriptor);
    entityRegistry.register('ProductionOrderMaterial', () => productionOrderMaterialDescriptor);
    entityRegistry.register('ProductionTicket', () => productionTicketDescriptor);
    entityRegistry.register('ProductionRun', () => productionRunDescriptor);
    entityRegistry.register('ProductionStop', () => productionStopDescriptor);
    entityRegistry.register('ProductionEntry', () => productionEntryDescriptor);
    entityRegistry.register('ProductionEntryMaterial', () => productionEntryMaterialDescriptor);
    // Catalog domain — party
    entityRegistry.register('Party', () => partyDescriptor);
    // Catalog domain — operation subcollections
    entityRegistry.register('OperationMachine', () => operationMachineDescriptor);
    entityRegistry.register('TechnicalSheetRouteMachine', () => technicalSheetRouteMachineDescriptor);
    // Catalog domain — technical sheet overrides
    entityRegistry.register('TechnicalSheetOverride', () => technicalSheetOverrideDescriptor);
    entityRegistry.register('TechnicalSheetOverrideProcess', () => technicalSheetOverrideProcessDescriptor);
    entityRegistry.register('TechnicalSheetOverrideInput', () => technicalSheetOverrideInputDescriptor);
    // Catalog domain — product variants
    entityRegistry.register('ProductVariant', () => productVariantDescriptor);
    // Catalog domain — variation options (OPTION axis)
    entityRegistry.register('VariationOption', () => variationOptionDescriptor);
    // Catalog domain — units of measure (default for Product.quantityDecimals)
    entityRegistry.register('Unit', () => unitDescriptor);
    // Catalog domain - reusable packaging templates (0160)
    entityRegistry.register('StandardPackaging', () => standardPackagingDescriptor);
    // Catalog domain — product-unit conversion factors (0118)
    entityRegistry.register('ProductUnit', () => productUnitDescriptor);
    // Catalog domain — size grids + occurrences
    entityRegistry.register('SizeGrid', () => sizeGridDescriptor);
    entityRegistry.register('SizeOccurrence', () => sizeOccurrenceDescriptor);
    entityRegistry.register('WorkCalendar', () => workCalendarDescriptor);
    entityRegistry.register('WorkShift', () => workShiftDescriptor);
    entityRegistry.register('WorkCalendarException', () => workCalendarExceptionDescriptor);
    // Purchasing domain
    entityRegistry.register('PurchaseOrder', () => purchaseOrderDescriptor);
    entityRegistry.register('PurchaseOrderItem', () => purchaseOrderItemDescriptor);
    entityRegistry.register('PurchaseOrderItemBreakdown', () => purchaseOrderItemBreakdownDescriptor);
    entityRegistry.register('GoodsReceipt', () => goodsReceiptDescriptor);
    entityRegistry.register('GoodsReceiptItem', () => goodsReceiptItemDescriptor);
    entityRegistry.register('GoodsReceiptItemBreakdown', () => goodsReceiptItemBreakdownDescriptor);
    entityRegistry.register('Shipment', () => shipmentDescriptor);
    entityRegistry.register('ShipmentItem', () => shipmentItemDescriptor);
    entityRegistry.register('ShipmentItemBreakdown', () => shipmentItemBreakdownDescriptor);
    // Fiscal domain
    entityRegistry.register('Invoice', () => invoiceDescriptor);
    entityRegistry.register('InvoiceItem', () => invoiceItemDescriptor);
    entityRegistry.register('InvoiceImport', () => invoiceImportDescriptor);
    entityRegistry.register('SupplierProductMap', () => supplierProductMapDescriptor);
    entityRegistry.register('ExternalRef', () => externalRefDescriptor);
    entityRegistry.register('WarehouseMap', () => warehouseMapDescriptor);
    // Print registry
    entityRegistry.register('Printer', () => printerDescriptor);
    // AI gateway metering
    entityRegistry.register('AiUsageLog', () => aiUsageLogDescriptor);
    entityRegistry.register('AiUsageRollup', () => aiUsageRollupDescriptor);
}
// Re-export descriptors for direct access if needed
export { userDataDescriptor, organizationUserOrgDataDescriptor } from './user.descriptor.js';
export { organizationDataDescriptor, userOrganizationDescriptor, } from './organization.descriptor.js';
export { profileDataDescriptor, userProfileDescriptor } from './profile.descriptor.js';
export { moduleDescriptor, organizationModuleDescriptor, themeSettingDescriptor } from './module.descriptor.js';
export { planDescriptor } from './plan.descriptor.js';
export { moduleGroupDescriptor } from './moduleGroup.descriptor.js';
export { productDescriptor } from './product.descriptor.js';
export { productGroupDescriptor } from './product-group.descriptor.js';
export { brandDescriptor } from './brand.descriptor.js';
export { savedSelectionDescriptor } from './saved-selection.descriptor.js';
export { sectorDescriptor } from './sector.descriptor.js';
export { factoryDescriptor } from './factory.descriptor.js';
export { productionStopReasonDescriptor } from './production-stop-reason.descriptor.js';
export { operationDescriptor } from './operation.descriptor.js';
export { machineDescriptor } from './machine.descriptor.js';
export { machineGroupDescriptor } from './machine-group.descriptor.js';
export { processDescriptor } from './process.descriptor.js';
export { componentDescriptor } from './component.descriptor.js';
export { globalConfigDescriptor, sessionDescriptor, } from './system.descriptor.js';
export { integrationConfigDescriptor } from './integration.descriptor.js';
export { ticketWorkspaceDescriptor, ticketWorkflowStageDescriptor, ticketAutomationRuleDescriptor, ticketAutomationExecutionDescriptor, } from './ticket-workspace.descriptor.js';
export { workspaceTicketDescriptor, ticketMessageDescriptor, ticketAttachmentDescriptor, ticketParticipantDescriptor, ticketRelationDescriptor, ticketLinkDescriptor, ticketActivityLogDescriptor, } from './workspace-ticket.descriptor.js';
export { tagDescriptor } from './tag.descriptor.js';
export { tagLinkDescriptor } from './tag-link.descriptor.js';
export { colorDescriptor } from './color.descriptor.js';
export { technicalSheetDescriptor } from './technical-sheet.descriptor.js';
export { technicalSheetRouteDescriptor } from './technical-sheet-route.descriptor.js';
export { technicalSheetMaterialDescriptor } from './technical-sheet-material.descriptor.js';
export { costCenterDescriptor } from './cost-center.descriptor.js';
export { distributionCenterDescriptor } from './distribution-center.descriptor.js';
export { stockLocationDescriptor } from './stock-location.descriptor.js';
export { storageAddressDescriptor } from './storage-address.descriptor.js';
export { storageAddressPatternDescriptor } from './storage-address-pattern.descriptor.js';
export { lotDescriptor } from './lot.descriptor.js';
export { stockMovementDescriptor } from './stock-movement.descriptor.js';
export { auditOperationDescriptor } from './audit-operation.descriptor.js';
export { auditFieldDescriptor } from './audit-field.descriptor.js';
export { stockBalanceDescriptor } from './stock-balance.descriptor.js';
export { stockLotBalanceDescriptor } from './stock-lot-balance.descriptor.js';
export { stockReservationDescriptor } from './stock-reservation.descriptor.js';
export { handlingUnitDescriptor } from './handling-unit.descriptor.js';
export { handlingUnitMovementDescriptor } from './handling-unit-movement.descriptor.js';
export { stockCountDescriptor } from './stock-count.descriptor.js';
export { stockCountEntryDescriptor } from './stock-count-entry.descriptor.js';
export { warehouseTaskTypeDescriptor } from './warehouse-task-type.descriptor.js';
export { warehouseTaskDescriptor } from './warehouse-task.descriptor.js';
export { warehouseTaskLineDescriptor } from './warehouse-task-line.descriptor.js';
export { salesOrderDescriptor } from './sales-order.descriptor.js';
export { salesOrderItemDescriptor } from './sales-order-item.descriptor.js';
export { salesOrderItemBreakdownDescriptor } from './sales-order-item-breakdown.descriptor.js';
export { productionOrderDescriptor } from './production-order.descriptor.js';
export { productionOrderBreakdownDescriptor } from './production-order-breakdown.descriptor.js';
export { productionOrderStepDescriptor } from './production-order-step.descriptor.js';
export { productionOrderMaterialDescriptor } from './production-order-material.descriptor.js';
export { productionTicketDescriptor } from './production-ticket.descriptor.js';
export { productionRunDescriptor } from './production-run.descriptor.js';
export { productionStopDescriptor } from './production-stop.descriptor.js';
export { productionEntryDescriptor } from './production-entry.descriptor.js';
export { productionEntryMaterialDescriptor } from './production-entry-material.descriptor.js';
export { partyDescriptor } from './party.descriptor.js';
export { operationMachineDescriptor } from './operation-machine.descriptor.js';
export { technicalSheetRouteMachineDescriptor } from './technical-sheet-route-machine.descriptor.js';
export { technicalSheetOverrideDescriptor } from './technical-sheet-override.descriptor.js';
export { technicalSheetOverrideProcessDescriptor } from './technical-sheet-override-process.descriptor.js';
export { technicalSheetOverrideInputDescriptor } from './technical-sheet-override-input.descriptor.js';
export { productVariantDescriptor } from './product-variant.descriptor.js';
export { variationOptionDescriptor } from './variation-option.descriptor.js';
export { unitDescriptor } from './unit.descriptor.js';
export { standardPackagingDescriptor } from './standard-packaging.descriptor.js';
export { productUnitDescriptor } from './product-unit.descriptor.js';
export { sizeGridDescriptor } from './size-grid.descriptor.js';
export { sizeOccurrenceDescriptor } from './size-occurrence.descriptor.js';
export { workCalendarDescriptor } from './work-calendar.descriptor.js';
export { workShiftDescriptor } from './work-shift.descriptor.js';
export { workCalendarExceptionDescriptor } from './work-calendar-exception.descriptor.js';
export { purchaseOrderDescriptor } from './purchase-order.descriptor.js';
export { purchaseOrderItemDescriptor } from './purchase-order-item.descriptor.js';
export { purchaseOrderItemBreakdownDescriptor } from './purchase-order-item-breakdown.descriptor.js';
export { goodsReceiptDescriptor } from './goods-receipt.descriptor.js';
export { goodsReceiptItemDescriptor } from './goods-receipt-item.descriptor.js';
export { goodsReceiptItemBreakdownDescriptor } from './goods-receipt-item-breakdown.descriptor.js';
export { shipmentDescriptor } from './shipment.descriptor.js';
export { shipmentItemDescriptor } from './shipment-item.descriptor.js';
export { shipmentItemBreakdownDescriptor } from './shipment-item-breakdown.descriptor.js';
export { invoiceDescriptor } from './invoice.descriptor.js';
export { invoiceItemDescriptor } from './invoice-item.descriptor.js';
export { invoiceImportDescriptor } from './invoice-import.descriptor.js';
export { supplierProductMapDescriptor } from './supplier-product-map.descriptor.js';
export { externalRefDescriptor } from './external-ref.descriptor.js';
export { warehouseMapDescriptor } from './warehouse-map.descriptor.js';
export { printerDescriptor } from './printer.descriptor.js';
export { aiUsageLogDescriptor, aiUsageRollupDescriptor } from './ai.descriptor.js';
export { preferenceDescriptors, PREFERENCE_ENTITY, } from './preferences.descriptor.js';
export { notificationDescriptors, NOTIFICATION_ENTITY, } from './notifications.descriptor.js';
export { FIELD_GROUPS, FIELD_GROUP_LABELS, findUnknownFieldGroups, } from './fieldGroups.js';
export { AUDIT_FIELD_DESCRIPTORS, AUDIT_FIELD_NAMES, AUDIT_FOREIGN_KEYS, AUDITABLE_ENTITY_NAMES, } from './_auditFields.js';
export { addressFieldDescriptors } from './_addressFields.js';
export { VERSION_FIELD_DESCRIPTOR, VERSIONED_ENTITY_NAMES, } from './_versionField.js';
export { LIFECYCLE_FIELD_DESCRIPTORS } from './_lifecycleFields.js';
export { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE, SYSTEM_ALLOWED_DATA_SOURCES, } from './dataSourcePolicy.js';
export { getDescriptorForRule, setDescriptorLookupStrict, resetDescriptorLookupState, } from './descriptorLookup.js';
// Re-export entity dictionary primitives for consumers (e.g. system-api /pg/v1 layer)
export { DataSourceStrategy, entityRegistry, getEffectiveAllowedDataSources, } from '@rammetec/data-access';
//# sourceMappingURL=index.js.map